import { Box, Stack, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import api from "../../../api";
import useLoading from "../../../context/Loading/useLoading";
import { getBackendUrl } from "../../../util/envFn";
import FileUploadStep from "./FileUploadStep";
import PreviewStep from "./PreviewStep";
import SuccessStep from "./SuccessStep";
import { ExcelData } from "./utils";

const steps = ['Select & Upload', 'Preview', 'Success'];

const AdminPanelImportData = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [importType, setImportType] = useState<'jobs' | 'users'>('jobs');
    const [importFormat, setImportFormat] = useState<string>('format1');
    const [previewData, setPreviewData] = useState<ExcelData[]>([]);
    const { loading, setLoading } = useLoading();
    const [fileName, setFileName] = useState<string>('');

    const handleSubmit = async () => {
        if (previewData.length === 0) {
            enqueueSnackbar('No data to import', { variant: 'warning' });
            return;
        }

        setLoading(true);
        const url = importType === 'jobs' ? '/jobs/create-by-list' : '/users/create-by-list';
        api.post(getBackendUrl(url), { data: previewData })
            .then(() => {
                setActiveStep(2);
                enqueueSnackbar(`${importType === 'jobs' ? 'Jobs' : 'Users'} imported successfully`, { variant: 'success' });
            })
            .catch((error) => {
                enqueueSnackbar(`Error importing ${importType}`, { variant: 'error' });
                console.error(`Error importing ${importType}:`, error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleReset = () => {
        setActiveStep(0);
        setPreviewData([]);
        setFileName('');
    };

    const renderStepContent = (step: number) => {
        switch (step) {
            case 0:
                return (
                    <FileUploadStep
                        importType={importType}
                        importFormat={importFormat}
                        onImportTypeChange={(type) => {
                            setImportType(type);
                            handleReset();
                        }}
                        onImportFormatChange={(format) => {
                            setImportFormat(format);
                            handleReset();
                        }}
                        fileName={fileName}
                        onFileNameChange={setFileName}
                        onPreviewDataChange={setPreviewData}
                        onStepChange={setActiveStep}
                        onReset={handleReset}
                    />
                );
            case 1:
                return (
                    <PreviewStep
                        previewData={previewData}
                        loading={loading}
                        onStepChange={setActiveStep}
                        onSubmit={handleSubmit}
                        onPreviewDataChange={setPreviewData}
                    />
                );
            case 2:
                return (
                    <SuccessStep
                        importType={importType}
                        previewDataLength={previewData.length}
                        onReset={handleReset}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Stack gap={4} pb={8}>
            <Typography variant="h4" mb={4}>Import Data</Typography>

            <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label, index) => (
                    <Step key={index}>
                        <StepLabel>
                            <Typography color={activeStep === index ? 'primary' : 'text.secondary'}>{label}</Typography>
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>

            <Box sx={{ mt: 4 }}>
                {renderStepContent(activeStep)}
            </Box>
        </Stack>
    );
};

export default AdminPanelImportData;

